'use client';

import { c as _c } from "react/compiler-runtime";
import { BI_APP_ID, BI_CATEGORY_PAGE_VIEW, BI_EVENT_OPEN_PAGE, BI_SDK_LOADED_EVENT, BI_VERSION } from '@/constants/bi-sdk';
import { RECOMMEND_VISITOR_ID_COOKIE_KEY } from '@/constants/recommend';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useUAContext } from '@/context/UAContext';
import { getClientCookie } from '@/lib/client-cookie';
import { getLocationHrefUrl } from '@/lib/url';
import { reportBiEventsAction } from '@/server/lib/bi';
import { usePostHog } from '@posthog/next';
import * as Sentry from '@sentry/nextjs';
import Script from 'next/script';
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from 'react';
const getTMS = () => Date.now();
const getTZ = () => new Date().getTimezoneOffset() * -1 * 60 * 1000;
type LoggerOptions = {
  ev: string;
  category: string;
  account_id?: string;
  user_agent?: string;
  a1?: string;
  a2?: string;
  a3?: string;
  a4?: string;
  a5?: string;
  a6?: string;
  a7?: string;
  a8?: string;
  a9?: string;
  a12?: string;
  a13?: string;
  a14?: string;
  a15?: string;
  a16?: string | null;
  a17?: string;
};
type ServerLoggerOptions = LoggerOptions;
type BISdkContextType = {
  sendBIReport: (options: LoggerOptions) => void;
  sendServerBIReport: (options: ServerLoggerOptions) => void;
  isLoadedBISdk: boolean;
  isBISdkFailed: boolean;
  isPartnerPromise: Promise<boolean>;
};
const BISdkContext = createContext<BISdkContextType | null>(null);
type BISdkProviderProps = PropsWithChildren<{
  isPartnerPromise: Promise<boolean>;
}>;
export const BISdkProvider = t0 => {
  const $ = _c(23);
  const {
    children,
    isPartnerPromise
  } = t0;
  const [biSdkLogger, setBISdkLogger] = useState(null);
  const [isBISdkFailed, setIsBISdkFailed] = useState(false);
  const {
    accountId,
    appConfig
  } = useConfigsContext();
  const {
    device
  } = useUAContext();
  const biSdkUri = appConfig?.features?.bi_sdk_uri;
  const isLoadedBISdk = biSdkLogger !== null;
  const reportBISdkFailure = _temp;
  let t1;
  if ($[0] !== accountId || $[1] !== biSdkLogger || $[2] !== device) {
    t1 = options => {
      if (biSdkLogger === null) {
        return;
      }
      biSdkLogger.log({
        ...options,
        bi_data_point_version: BI_VERSION,
        tms: getTMS(),
        tz: getTZ(),
        account_id: accountId,
        a0: device
      });
    };
    $[0] = accountId;
    $[1] = biSdkLogger;
    $[2] = device;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const sendBIReport = t1;
  let t2;
  if ($[4] !== biSdkLogger || $[5] !== device) {
    t2 = options_0 => {
      if (biSdkLogger === null) {
        return;
      }
      reportBiEventsAction({
        csdataBid: getClientCookie(RECOMMEND_VISITOR_ID_COOKIE_KEY),
        events: [{
          ...options_0,
          a0: device,
          tms: getTMS(),
          tz: getTZ()
        }]
      }).catch(_temp2);
    };
    $[4] = biSdkLogger;
    $[5] = device;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const sendServerBIReport = t2;
  const handleLoad = _temp3;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      console.log("\uD83D\uDD25 BISdk ready");
      if (typeof VrBiJs === "undefined") {
        console.error("BISdk onReady but VrBiJs is unavailable");
        reportBISdkFailure("ready_without_global");
        setIsBISdkFailed(true);
        return;
      }
      const biSdkLogger_0 = VrBiJs.getLogger(BI_APP_ID);
      setIsBISdkFailed(false);
      setBISdkLogger(biSdkLogger_0);
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleReady = t3;
  let t4;
  if ($[8] !== biSdkUri) {
    t4 = event => {
      console.error("BISdk script failed to load", event);
      reportBISdkFailure("script_load_error", {
        src: biSdkUri,
        eventType: event.type
      });
      setIsBISdkFailed(true);
    };
    $[8] = biSdkUri;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleError = t4;
  let t5;
  if ($[10] !== isBISdkFailed || $[11] !== isLoadedBISdk || $[12] !== isPartnerPromise || $[13] !== sendBIReport || $[14] !== sendServerBIReport) {
    t5 = {
      sendBIReport,
      sendServerBIReport,
      isLoadedBISdk,
      isBISdkFailed,
      isPartnerPromise
    };
    $[10] = isBISdkFailed;
    $[11] = isLoadedBISdk;
    $[12] = isPartnerPromise;
    $[13] = sendBIReport;
    $[14] = sendServerBIReport;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== biSdkUri || $[17] !== handleError) {
    t6 = biSdkUri && <Script src={biSdkUri} onLoad={handleLoad} onError={handleError} onReady={handleReady} />;
    $[16] = biSdkUri;
    $[17] = handleError;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== children || $[20] !== t5 || $[21] !== t6) {
    t7 = <BISdkContext.Provider value={t5}>{children}{t6}</BISdkContext.Provider>;
    $[19] = children;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
};
export const useBISdkContext = () => {
  const value = useContext(BISdkContext);
  if (value == null) {
    throw new Error("useBISdkContext cannot be used outside of BISdkProvider");
  }
  return value;
};
export const useBIPageView = extraData => {
  const $ = _c(12);
  const {
    accountId,
    accountType
  } = useConfigsContext();
  const {
    isLoadedBISdk,
    isPartnerPromise,
    sendBIReport
  } = useBISdkContext();
  const posthog = usePostHog();
  let t0;
  if ($[0] !== extraData) {
    t0 = extraData || {};
    $[0] = extraData;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    a14,
    isVoucherRoom
  } = t0;
  let t1;
  let t2;
  if ($[2] !== a14 || $[3] !== accountId || $[4] !== accountType || $[5] !== isLoadedBISdk || $[6] !== isPartnerPromise || $[7] !== isVoucherRoom || $[8] !== posthog || $[9] !== sendBIReport) {
    t1 = () => {
      if (isLoadedBISdk) {
        const send = async () => {
          let a15 = "";
          ;
          try {
            if (isVoucherRoom) {
              a15 = "nexus";
            } else {
              if (await isPartnerPromise) {
                a15 = "creator_partnership";
              }
            }
          } catch (t3) {
            const error = t3;
            console.error("Failed to send BI page view", error);
          }
          sendBIReport({
            ev: BI_EVENT_OPEN_PAGE,
            category: BI_CATEGORY_PAGE_VIEW,
            account_id: accountId,
            a1: accountType,
            a4: getLocationHrefUrl(),
            a13: posthog.get_distinct_id(),
            a14,
            a15
          });
        };
        send();
      }
    };
    t2 = [isLoadedBISdk, sendBIReport, accountId, accountType, a14, isVoucherRoom, isPartnerPromise, posthog];
    $[2] = a14;
    $[3] = accountId;
    $[4] = accountType;
    $[5] = isLoadedBISdk;
    $[6] = isPartnerPromise;
    $[7] = isVoucherRoom;
    $[8] = posthog;
    $[9] = sendBIReport;
    $[10] = t1;
    $[11] = t2;
  } else {
    t1 = $[10];
    t2 = $[11];
  }
  useEffect(t1, t2);
};
function _temp(reason, extra) {
  Sentry.captureMessage("BI SDK initialization failed", {
    level: "warning",
    tags: {
      feature: "bi-sdk",
      reason
    },
    extra
  });
}
function _temp2() {}
function _temp3() {
  console.log("\u2705 BISdk loaded");
  window.dispatchEvent(new CustomEvent(BI_SDK_LOADED_EVENT));
}